import * as THREE from 'three';

/**
 * Closest point on a closed curve (uniform t sampling).
 * @param {THREE.Curve} curve
 * @param {THREE.Vector3} point
 * @param {number} [samples]
 */
export function findClosestPointOnCurve(curve, point, samples = 500) {
  let bestT = 0;
  let bestDist = Infinity;
  const bestPoint = new THREE.Vector3();

  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const p = curve.getPointAt(t);
    const d = p.distanceToSquared(point);
    if (d < bestDist) {
      bestDist = d;
      bestT = t;
      bestPoint.copy(p);
    }
  }

  return { t: bestT, position: bestPoint, distance: Math.sqrt(bestDist) };
}

/**
 * @param {THREE.Curve} curve
 * @param {{ t: number }[]} autoCorners
 * @param {number[]} customTs
 * @param {{ useAuto?: boolean; useCustom?: boolean; minSpacing?: number }} options
 */
export function resolveCornerList(curve, autoCorners, customTs, options = {}) {
  const { useAuto = true, useCustom = true, minSpacing = 0.025 } = options;

  const entries = [];
  if (useAuto) {
    for (const c of autoCorners) entries.push({ t: c.t });
  }
  if (useCustom) {
    for (const t of customTs) entries.push({ t });
  }

  entries.sort((a, b) => a.t - b.t);

  const merged = [];
  for (const e of entries) {
    if (merged.length === 0 || e.t - merged[merged.length - 1].t > minSpacing) {
      merged.push(e);
    }
  }

  return merged.map((e) => ({
    t: e.t,
    position: curve.getPointAt(e.t),
  }));
}

/**
 * Raycast to horizontal plane at elevation Y.
 * @param {THREE.Raycaster} raycaster
 * @param {number} elevationY
 */
export function intersectTrackPlane(raycaster, elevationY) {
  const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -elevationY);
  const hit = new THREE.Vector3();
  return raycaster.ray.intersectPlane(plane, hit) ? hit : null;
}

const _curveScreenPoint = new THREE.Vector3();

/**
 * World- and screen-space tolerances for snapping clicks to the circuit silhouette.
 * @param {Partial<import('./settings.js').HologramSettings>} [settings]
 */
export function getTrackPickTolerance(settings = {}) {
  const strokeReach =
    (settings.strokeSpacing ?? 0.042) +
    Math.max(
      settings.outerStrokeWeight ?? 0.009,
      settings.centerStrokeWeight ?? 0.014,
    ) *
      0.5;
  const curtainHalfWidth = 0.055;
  const maxWorldDistance = Math.max(strokeReach, curtainHalfWidth) + 0.025;

  return { maxWorldDistance, maxScreenDistance: 24 };
}

/**
 * Snap a pointer position to the nearest point on the track centerline.
 * @param {PointerEvent} event
 * @param {HTMLCanvasElement} canvas
 * @param {THREE.Camera} camera
 * @param {THREE.Raycaster} raycaster
 * @param {THREE.Vector2} pointerNdc
 * @param {THREE.Curve} curve
 * @param {number} elevationY
 * @param {{ maxWorldDistance?: number; maxScreenDistance?: number }} [pickTolerance]
 * @returns {{ t: number; distance: number } | null}
 */
export function pickTrackParamFromPointer(
  event,
  canvas,
  camera,
  raycaster,
  pointerNdc,
  curve,
  elevationY,
  pickTolerance = {},
) {
  if (!curve) return null;

  const { maxWorldDistance = 0.15, maxScreenDistance = 24 } = pickTolerance;

  const rect = canvas.getBoundingClientRect();
  pointerNdc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  pointerNdc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  raycaster.setFromCamera(pointerNdc, camera);
  const hit = intersectTrackPlane(raycaster, elevationY);
  if (!hit) return null;

  const { t, position, distance } = findClosestPointOnCurve(curve, hit);
  if (distance > maxWorldDistance) return null;

  _curveScreenPoint.copy(position).project(camera);
  const screenDx = (_curveScreenPoint.x - pointerNdc.x) * (rect.width * 0.5);
  const screenDy = (_curveScreenPoint.y - pointerNdc.y) * (rect.height * 0.5);
  if (Math.hypot(screenDx, screenDy) > maxScreenDistance) return null;

  return { t, distance };
}

/**
 * Rotate corner entries so the first item is at/after startT on the lap.
 * @param {{ marker?: THREE.Object3D; t: number }[]} corners
 * @param {number} startT
 * @param {boolean} [clockwise]
 */
export function rotateCornersFromStart(corners, startT, clockwise = true) {
  const sorted = [...corners].sort((a, b) => a.t - b.t);
  const i = sorted.findIndex((c) => c.t >= startT);
  const pivot = i === -1 ? 0 : i;
  const rotated = [...sorted.slice(pivot), ...sorted.slice(0, pivot)];
  if (clockwise) return rotated;
  if (rotated.length <= 1) return rotated;
  return [rotated[0], ...rotated.slice(1).reverse()];
}

/**
 * Order markers from the start flag line along the lap direction.
 * @param {THREE.Object3D[]} markers
 * @param {{ startT?: number | null; clockwise?: boolean; minSpacing?: number }} [options]
 * @returns {{ marker: THREE.Object3D; t: number }[]}
 */
export function orderMarkersFromStart(
  markers,
  { startT = null, clockwise = true, minSpacing = 0.025 } = {},
) {
  const entries = markers.map((marker) => ({
    marker,
    t: marker.userData.cornerT ?? 0,
  }));

  if (startT == null || !Number.isFinite(startT)) {
    return entries.sort((a, b) => a.t - b.t);
  }

  const filtered = entries.filter((c) => Math.abs(c.t - startT) >= minSpacing);
  return rotateCornersFromStart(filtered, startT, clockwise);
}
